import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

const formatRegistrarStatus = (status) =>
  String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const getDomainSuccessCopy = (paymentData) => {
  const status = paymentData?.domainRegistration?.status;

  if (paymentData?.orderType !== "domain" || !status) {
    return {
      title: "Payment Successful",
      subtitle: "Your order has been received successfully.",
    };
  }

  if (status === "registered") {
    return {
      title: "Payment Successful - Domain Registered",
      subtitle: "Your payment and domain registration were completed successfully.",
    };
  }

  if (status === "queued") {
    return {
      title: "Payment Successful - Registration Submitted",
      subtitle:
        "Your payment is confirmed and registrar processing is in progress.",
    };
  }

  return {
    title: "Payment Successful - Registration Follow-up Needed",
    subtitle:
      "Your payment is confirmed, but domain registration needs team follow-up.",
  };
};

const getRegistrarStatusBadge = (status) => {
  if (status === "registered") {
    return {
      label: "Registrar: Registered",
      className: "border-emerald-300/40 bg-emerald-500/15 text-emerald-200",
    };
  }

  if (status === "queued") {
    return {
      label: "Registrar: In Progress",
      className: "border-amber-300/40 bg-amber-500/15 text-amber-200",
    };
  }

  if (!status) {
    return null;
  }

  return {
    label: "Registrar: Follow-up Needed",
    className: "border-rose-300/40 bg-rose-500/15 text-rose-200",
  };
};

const getRegistrarStatusTextClass = (status) => {
  if (status === "registered") {
    return "text-emerald-300";
  }

  if (status === "queued") {
    return "text-amber-300";
  }

  if (!status) {
    return "text-sky-300";
  }

  return "text-rose-300";
};

const getNextSteps = (paymentData) => {
  if (paymentData?.setupPlan?.length) {
    return paymentData.setupPlan.map((step) => step.label || step);
  }

  if (paymentData?.orderType === "email") {
    return paymentData.domainSource === "external"
      ? [
          "Mailbox is provisioned and credentials are issued.",
          "Add MX, SPF, DKIM, and DMARC records at your DNS provider.",
          "Inbound and outbound delivery becomes fully active after DNS propagation.",
        ]
      : [
          "Mailbox is provisioned and active on managed DNS.",
          "DNS records are configured in the managed zone.",
          "Use the credentials below and change temporary password on first login.",
        ];
  }

  if (paymentData?.orderType === "ssl") {
    return paymentData.domainSource === "external"
      ? [
          "We will send the DNS or HTTP validation record.",
          "Certificate issuance starts after validation passes.",
          "SSL is installed on the hosting target and renewal is monitored.",
        ]
      : [
          "We will confirm website DNS records.",
          "SSL is issued through hosting or ACME automation.",
          "Automatic renewal monitoring is enabled.",
        ];
  }

  return [
    "Our team will review your booking.",
    paymentData?.domainRegistration?.status === "registered"
      ? "Domain registration has been submitted successfully through GoDaddy."
      : "Domain registration and DNS setup will be processed shortly.",
    "You will receive updates via email.",
  ];
};

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const paymentData = location.state || {};
  const nextSteps = getNextSteps(paymentData);
  const domainCopy = getDomainSuccessCopy(paymentData);
  const registrarStatusLabel = formatRegistrarStatus(paymentData?.domainRegistration?.status);
  const registrarBadge = getRegistrarStatusBadge(paymentData?.domainRegistration?.status);
  const registrarStatusTextClass = getRegistrarStatusTextClass(paymentData?.domainRegistration?.status);
  const domainWarning =
    paymentData?.warning || paymentData?.domainRegistration?.message || "";

  return (
    <main className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-950 via-emerald-950 to-slate-900 px-4 py-20 text-white">
      <section className="w-full max-w-3xl rounded-[35px] border border-emerald-400/20 bg-slate-900/70 p-10 text-center shadow-2xl backdrop-blur-xl">
        <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-emerald-500/20 text-5xl">
          OK
        </div>

        <h1 className="mt-8 text-5xl font-black text-emerald-300">
          {domainCopy.title}
        </h1>

        <p className="mt-4 text-slate-300">
          {domainCopy.subtitle}
        </p>

        {paymentData?.orderType === "domain" && registrarBadge ? (
          <div className="mt-5 flex justify-center">
            <span
              className={`inline-flex items-center rounded-full border px-4 py-2 text-xs font-bold uppercase tracking-[0.12em] ${registrarBadge.className}`}
            >
              {registrarBadge.label}
            </span>
          </div>
        ) : null}

        {domainWarning ? (
          <div className="mt-6 rounded-2xl border border-amber-400/30 bg-amber-500/10 p-4 text-left text-sm text-amber-200">
            {domainWarning}
          </div>
        ) : null}

        <div className="mt-10 rounded-2xl border border-white/10 bg-slate-950/60 p-6 text-left">
          {[
            ["Service", paymentData.serviceName],
            ["Domain", paymentData.domain],
            ["Requested Email", paymentData.requestedEmailAddress],
            ["Booking ID", paymentData.bookingId],
            ["Payment ID", paymentData.paymentId],
            ["Credentials Delivery", paymentData.credentialsDelivery?.status],
            ["Registrar Status", registrarStatusLabel],
            ["GoDaddy Order ID", paymentData.domainRegistration?.providerOrderId],
            ["Amount Paid", paymentData.amount ? `Rs. ${paymentData.amount}` : ""],
          ]
            .filter(([, value]) => value)
            .map(([label, value]) => (
              <div
                key={label}
                className="flex items-center justify-between gap-6 border-b border-white/10 py-4 first:pt-0 last:border-b-0 last:pb-0"
              >
                <span className="text-slate-400">{label}</span>
                <span
                  className={`text-right font-bold ${
                    label === "Registrar Status" ? registrarStatusTextClass : "text-sky-300"
                  }`}
                >
                  {value}
                </span>
              </div>
            ))}
        </div>

        <div className="mt-8 rounded-2xl border border-sky-400/20 bg-sky-500/10 p-5 text-left">
          <h3 className="text-lg font-bold text-sky-300">Next Steps</h3>

          <ul className="mt-4 space-y-3 text-sm text-slate-300">
            {nextSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
            <li>Support: info@asiduo.com</li>
          </ul>
        </div>

        {paymentData.dnsRecordsNeeded?.length ? (
          <div className="mt-8 rounded-2xl border border-white/10 bg-slate-950/60 p-5 text-left">
            <h3 className="text-lg font-bold text-emerald-300">
              DNS Records To Prepare
            </h3>

            <div className="mt-4 space-y-3 text-sm text-slate-300">
              {paymentData.dnsRecordsNeeded.map((record, index) => (
                <div key={`${record.type}-${record.host}-${index}`} className="rounded-xl bg-white/5 p-4">
                  <p className="font-bold text-white">
                    {record.type} {record.host ? `- ${record.host}` : ""}
                  </p>
                  <p className="mt-1 break-words">{record.value}</p>
                  <p className="mt-1 text-slate-400">{record.purpose}</p>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {paymentData.mailboxCredentials?.loginEmail ? (
          <div className="mt-8 rounded-2xl border border-emerald-300/30 bg-emerald-500/10 p-5 text-left">
            <h3 className="text-lg font-bold text-emerald-300">Mailbox Credentials</h3>

            <div className="mt-4 space-y-2 text-sm text-slate-200">
              <p>
                <span className="text-slate-400">Login Email:</span>{" "}
                {paymentData.mailboxCredentials.loginEmail}
              </p>
              <p>
                <span className="text-slate-400">Temporary Password:</span>{" "}
                {paymentData.mailboxCredentials.temporaryPassword}
              </p>
              {paymentData.mailboxCredentials.webmailUrl ? (
                <p>
                  <span className="text-slate-400">Webmail URL:</span>{" "}
                  <a
                    href={paymentData.mailboxCredentials.webmailUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sky-300 underline"
                  >
                    {paymentData.mailboxCredentials.webmailUrl}
                  </a>
                </p>
              ) : null}
              {paymentData.mailboxCredentials.imap?.host ? (
                <p>
                  <span className="text-slate-400">IMAP:</span>{" "}
                  {paymentData.mailboxCredentials.imap.host}:
                  {paymentData.mailboxCredentials.imap.port} ({paymentData.mailboxCredentials.imap.security})
                </p>
              ) : null}
              {paymentData.mailboxCredentials.smtp?.host ? (
                <p>
                  <span className="text-slate-400">SMTP:</span>{" "}
                  {paymentData.mailboxCredentials.smtp.host}:
                  {paymentData.mailboxCredentials.smtp.port} ({paymentData.mailboxCredentials.smtp.security})
                </p>
              ) : null}
              <p className="pt-2 text-xs text-amber-200">
                Change the temporary password immediately after first login.
              </p>
            </div>
          </div>
        ) : null}

        <button
          type="button"
          onClick={() => navigate("/")}
          className="mt-10 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-8 py-4 text-sm font-bold text-white"
        >
          Go To Homepage
        </button>
      </section>
    </main>
  );
}
